"use client";
import { withArticle } from "@/lib/utils";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import type { LeaderboardEntry } from "@/lib/leaderboard-utils";

interface LeaderboardClientProps {
  entries: LeaderboardEntry[];
  tradeSlug: string;
  tradeName: string;
  jobTypeSlug?: string;
}

export function LeaderboardClient({
  entries,
  tradeSlug,
  tradeName,
  jobTypeSlug,
}: LeaderboardClientProps) {
  const [postcode, setPostcode] = useState("");
  const [highlightedSlug, setHighlightedSlug] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const maxCostAvg = entries.length > 0 ? entries[0].costAvg : 1;

  useEffect(() => {
    if (highlightedSlug && rowRefs.current[highlightedSlug]) {
      rowRefs.current[highlightedSlug]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightedSlug]);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!postcode.trim()) return;

    setSearching(true);
    setHighlightedSlug(null);

    try {
      const res = await fetch(`/api/geocode-multiplier?postcode=${encodeURIComponent(postcode.trim())}`);
      if (res.ok) {
        const data = await res.json();
        if (data.locationSlug) {
          setHighlightedSlug(data.locationSlug);
        } else if (data.name) {
          const match = entries.find(
            (e) => e.location.name.toLowerCase() === data.name.toLowerCase()
          );
          if (match) setHighlightedSlug(match.location.slug);
        }
      }
    } catch {
      // Silently fail
    } finally {
      setSearching(false);
    }
  }

  return (
    <div>
      {/* Postcode search */}
      <div className="mb-8 p-5 bg-sage-pale rounded-md">
        <p className="text-text-body font-medium mb-2">
          Where does your city rank?
        </p>
        <form onSubmit={handleSearch} className="flex gap-3 max-w-md">
          <input
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="Enter your postcode"
            className="flex-1 px-4 py-2.5 border border-stone rounded-sm text-sm bg-white focus:outline-none focus:border-forest"
          />
          <button
            type="submit"
            disabled={searching}
            className="px-5 py-2.5 bg-forest text-white font-semibold rounded-sm text-sm hover:bg-forest-light transition-colors disabled:opacity-60"
          >
            {searching ? "Finding..." : "Find me"}
          </button>
        </form>
      </div>

      {/* Leaderboard table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" role="table">
          <thead>
            <tr className="border-b border-stone text-left">
              <th className="py-3 px-3 font-semibold text-charcoal w-12">#</th>
              <th className="py-3 px-3 font-semibold text-charcoal">City</th>
              <th className="py-3 px-3 font-semibold text-charcoal hidden sm:table-cell">Region</th>
              <th className="py-3 px-3 font-semibold text-charcoal text-right">Avg. Cost</th>
              <th className="py-3 px-3 font-semibold text-charcoal text-right w-24">vs Avg</th>
              <th className="py-3 px-3 font-semibold text-charcoal hidden md:table-cell w-40">
                <span className="sr-only">Cost bar</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const isHighlighted = highlightedSlug === entry.location.slug;
              const barWidth = Math.round((entry.costAvg / maxCostAvg) * 100);
              const isAboveAvg = entry.percentFromAvg > 0;
              const isAtAvg = entry.percentFromAvg === 0;

              return (
                <tr
                  key={entry.location.slug}
                  ref={(el) => { rowRefs.current[entry.location.slug] = el; }}
                  className={`border-b border-stone/50 transition-colors ${
                    isHighlighted
                      ? "bg-forest/10 ring-2 ring-forest ring-inset"
                      : "hover:bg-sage-pale/50"
                  }`}
                >
                  <td className="py-3 px-3 font-semibold text-text-muted">
                    {entry.rank}
                  </td>
                  <td className="py-3 px-3">
                    <Link
                      href={`/prices/${tradeSlug}/${entry.location.slug}`}
                      className="text-forest font-medium no-underline hover:underline"
                    >
                      {entry.location.name}
                    </Link>
                  </td>
                  <td className="py-3 px-3 text-text-muted hidden sm:table-cell">
                    {entry.location.region}
                  </td>
                  <td className="py-3 px-3 text-right font-medium text-charcoal">
                    {"£"}{entry.costLow.toLocaleString()} &ndash; {"£"}{entry.costHigh.toLocaleString()}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        isAtAvg
                          ? "bg-stone/30 text-text-muted"
                          : isAboveAvg
                          ? "bg-red-50 text-red-700"
                          : "bg-green-50 text-green-700"
                      }`}
                    >
                      {isAtAvg
                        ? "Avg"
                        : isAboveAvg
                        ? `+${entry.percentFromAvg}%`
                        : `${entry.percentFromAvg}%`}
                    </span>
                  </td>
                  <td className="py-3 px-3 hidden md:table-cell">
                    <div className="w-full bg-stone/30 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all ${
                          isAboveAvg ? "bg-copper" : "bg-sage"
                        }`}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cross-links */}
      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <Link
          href={`/prices/${tradeSlug}`}
          className="inline-flex items-center justify-center px-5 py-3 border border-forest text-forest font-semibold rounded-sm text-sm no-underline hover:bg-forest/5 transition-colors"
        >
          View {tradeName} Prices
        </Link>
        <Link
          href={`/compare${jobTypeSlug ? `?trade=${tradeSlug}` : `?trade=${tradeSlug}`}`}
          className="inline-flex items-center justify-center px-5 py-3 border border-forest text-forest font-semibold rounded-sm text-sm no-underline hover:bg-forest/5 transition-colors"
        >
          Compare Two Cities
        </Link>
        <Link
          href={`/find/${tradeSlug}`}
          className="inline-flex items-center justify-center px-5 py-3 bg-forest text-white font-semibold rounded-sm text-sm no-underline hover:bg-forest-light transition-colors"
        >
          Find {withArticle(tradeName)}
        </Link>
      </div>
    </div>
  );
}
